import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  assignCashEntitlementDisbursement,
  cancelCashEntitlement,
  getPendingCashEntitlementDisbursement,
} from "../../api/cashEntitlements";
import { mapCashEntitlementRow } from "../../utils/cashEntitlementMapper";
import {
  cashEntitlementSourceLabel,
  formatCashAmount,
  formatCashDate,
} from "../../utils/cashEntitlement";
import "../advance/AdvanceDisbursementRouting.css";

function assigneeOptionLabel(target) {
  const parts = [target.fullName];
  if (target.employeeCode) parts.push(`(${target.employeeCode})`);
  if (target.jobTitle) parts.push(`— ${target.jobTitle}`);
  if (target.branchName) parts.push(`— ${target.branchName}`);
  return parts.join(" ");
}

const NO_BRANCH = "__no_branch__";
const NO_DEPARTMENT = "__no_department__";

function uniqueSorted(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "ar"),
  );
}

export default function CashEntitlementRouting() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [targetById, setTargetById] = useState({});
  const [selectedById, setSelectedById] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [submittingId, setSubmittingId] = useState("");
  const [submittingAction, setSubmittingAction] = useState("");

  const loadRows = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    setLoadError("");
    try {
      const payload = await getPendingCashEntitlementDisbursement();
      const list = (Array.isArray(payload) ? payload : payload?.items || []).map(
        mapCashEntitlementRow,
      );
      setRows(list);
      setTargetById((prev) => {
        const next = {};
        for (const row of list) {
          const previous = prev[row.id];
          if (previous === "CASHIER" && row.canAssignToCashiers) {
            next[row.id] = "CASHIER";
          } else if (previous === "TREASURY") {
            next[row.id] = "TREASURY";
          } else {
            next[row.id] = row.canAssignToCashiers ? "CASHIER" : "TREASURY";
          }
        }
        return next;
      });
      setSelectedById((prev) => {
        const next = {};
        for (const row of list) {
          const targets = row.treasuryTargets || [];
          const previous = prev[row.id];
          const stillValid = targets.some((t) => t.employeeId === previous);
          if (stillValid) next[row.id] = previous;
          else if (targets.length === 1) next[row.id] = targets[0].employeeId;
          else next[row.id] = "";
        }
        return next;
      });
      setRowErrors({});
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر تحميل المستحقات بانتظار توجيه الصرف",
      );
      setRows([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const branchOptions = useMemo(() => {
    const names = uniqueSorted(rows.map((row) => row.branchName));
    const hasNoBranch = rows.some((row) => !row.branchName);
    return hasNoBranch ? [...names, NO_BRANCH] : names;
  }, [rows]);

  const departmentOptions = useMemo(() => {
    const scoped = branchFilter
      ? rows.filter((row) => (row.branchName || NO_BRANCH) === branchFilter)
      : rows;
    const names = uniqueSorted(scoped.map((row) => row.departmentName));
    const hasNoDepartment = scoped.some((row) => !row.departmentName);
    return hasNoDepartment ? [...names, NO_DEPARTMENT] : names;
  }, [rows, branchFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (branchFilter && (row.branchName || NO_BRANCH) !== branchFilter) {
        return false;
      }
      if (
        departmentFilter &&
        (row.departmentName || NO_DEPARTMENT) !== departmentFilter
      ) {
        return false;
      }
      if (!q) return true;
      return [
        row.employeeName,
        row.employeeCode,
        row.departmentName,
        row.branchName,
        row.reason,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [rows, search, branchFilter, departmentFilter]);

  const totalAmount = useMemo(
    () => filtered.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [filtered],
  );

  const submitAssign = async (row) => {
    const targetType = targetById[row.id] || "TREASURY";
    const assigneeEmployeeId =
      targetType === "TREASURY" ? selectedById[row.id] : undefined;
    if (targetType === "TREASURY" && !assigneeEmployeeId) {
      setRowErrors((prev) => ({
        ...prev,
        [row.id]: "اختر مسؤول الخزنة الذي سيصرف المستحقات",
      }));
      return;
    }
    if (targetType === "CASHIER" && !row.canAssignToCashiers) {
      setRowErrors((prev) => ({
        ...prev,
        [row.id]: "لا يوجد كاشير نشط في فرع الموظف",
      }));
      return;
    }

    setSubmittingId(row.id);
    setSubmittingAction("assign");
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    try {
      await assignCashEntitlementDisbursement(row.id, {
        targetType,
        assigneeEmployeeId,
      });
      await loadRows({ silent: true });
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [row.id]:
          err?.response?.data?.message ||
          err?.message ||
          "تعذر توجيه الصرف",
      }));
    } finally {
      setSubmittingId("");
      setSubmittingAction("");
    }
  };

  const submitCancel = async (row) => {
    if (!row?.id || submittingId) return;
    const ok = window.confirm(
      `إلغاء صرف مستحقات ${row.employeeName || "الموظف"} بمبلغ ${formatCashAmount(row.amount)}؟`,
    );
    if (!ok) return;
    setSubmittingId(row.id);
    setSubmittingAction("cancel");
    try {
      await cancelCashEntitlement(row.id);
      await loadRows({ silent: true });
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [row.id]:
          err?.response?.data?.message ||
          err?.message ||
          "تعذر إلغاء الصرف",
      }));
    } finally {
      setSubmittingId("");
      setSubmittingAction("");
    }
  };

  return (
    <div className="adv-disburse is-wide" dir="rtl">
      <div className="adv-disburse-header">
        <div>
          <h2 className="adv-disburse-title">توجيه صرف المستحقات النقدية</h2>
          <p className="adv-disburse-subtitle">
            اختر لكل بند: مسؤول الخزنة، أو كاشيرية فرع الموظف إن وُجد كاشير
            نشط. يمكنك إلغاء الصرف قبل التسليم.
          </p>
        </div>
        <button
          type="button"
          className="adv-disburse-refresh"
          onClick={() => loadRows()}
          disabled={loading || Boolean(submittingId)}
        >
          تحديث
        </button>
      </div>

      <div className="adv-disburse-toolbar">
        <input
          type="search"
          className="adv-disburse-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="بحث بالاسم أو الكود..."
          disabled={loading}
        />
        <select
          className="adv-disburse-filter-select"
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          disabled={loading || rows.length === 0}
        >
          <option value="">كل الفروع</option>
          {branchOptions.map((name) => (
            <option key={name} value={name}>
              {name === NO_BRANCH ? "بدون فرع (إدارة)" : name}
            </option>
          ))}
        </select>
        <select
          className="adv-disburse-filter-select"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          disabled={loading || rows.length === 0}
        >
          <option value="">كل الأقسام</option>
          {departmentOptions.map((name) => (
            <option key={name} value={name}>
              {name === NO_DEPARTMENT ? "بدون قسم" : name}
            </option>
          ))}
        </select>
        <span className="adv-disburse-count">
          {filtered.length} من {rows.length}
        </span>
      </div>

      {loadError ? (
        <div className="adv-disburse-error-banner">{loadError}</div>
      ) : loading ? (
        <div className="adv-disburse-empty">جاري التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="adv-disburse-empty">
          {rows.length === 0
            ? "لا توجد مستحقات بانتظار توجيه الصرف حالياً"
            : "لا توجد نتائج مطابقة للبحث أو الفلاتر"}
        </div>
      ) : (
        <div className="adv-disburse-table-wrap">
          <table className="adv-disburse-table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>الكود</th>
                <th>الفرع</th>
                <th>المبلغ</th>
                <th>المصدر</th>
                <th>تاريخ الإنشاء</th>
                <th>توجيه الصرف إلى</th>
                <th>إجراء</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const targetType = targetById[row.id] || "TREASURY";
                const treasuryTargets = row.treasuryTargets || [];
                const selected = selectedById[row.id] || "";
                const busy = submittingId === row.id;
                const error = rowErrors[row.id];
                const cashierOk = Boolean(row.canAssignToCashiers);
                const assignDisabled =
                  Boolean(submittingId) ||
                  (targetType === "CASHIER" && !cashierOk) ||
                  (targetType === "TREASURY" && !selected);

                return (
                  <tr key={row.id}>
                    <td className="is-name">
                      <div className="adv-disburse-name-cell">
                        <strong>{row.employeeName || "—"}</strong>
                        {row.reason ? (
                          <span className="adv-disburse-reason-hint">
                            {row.reason}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td>{row.employeeCode || "—"}</td>
                    <td>{row.branchName || "—"}</td>
                    <td>{formatCashAmount(row.amount)}</td>
                    <td>{cashEntitlementSourceLabel(row.source)}</td>
                    <td>{formatCashDate(row.createdAt)}</td>
                    <td className="is-assign">
                      <select
                        className="adv-disburse-inline-select"
                        value={targetType}
                        onChange={(e) =>
                          setTargetById((prev) => ({
                            ...prev,
                            [row.id]: e.target.value,
                          }))
                        }
                        disabled={busy}
                      >
                        <option value="TREASURY">مسؤول الخزنة</option>
                        <option value="CASHIER" disabled={!cashierOk}>
                          {cashierOk
                            ? `كاشيرية فرع ${row.branchName || "الفرع"} (${(row.cashierTargets || []).length})`
                            : "كاشيرية الفرع (غير متاحة)"}
                        </option>
                      </select>
                      {targetType === "TREASURY" ? (
                        treasuryTargets.length === 0 ? (
                          <span className="adv-disburse-empty-targets">
                            لا يوجد مسؤول خزنة نشط
                          </span>
                        ) : (
                          <select
                            className="adv-disburse-inline-select"
                            value={selected}
                            onChange={(e) =>
                              setSelectedById((prev) => ({
                                ...prev,
                                [row.id]: e.target.value,
                              }))
                            }
                            disabled={busy}
                          >
                            <option value="">— اختر مسؤول الخزنة —</option>
                            {treasuryTargets.map((target) => (
                              <option
                                key={target.employeeId}
                                value={target.employeeId}
                              >
                                {assigneeOptionLabel(target)}
                              </option>
                            ))}
                          </select>
                        )
                      ) : (
                        <span className="adv-disburse-branch-target">
                          كاشيرية فرع {row.branchName || "الفرع"}
                        </span>
                      )}
                      {error ? (
                        <div className="adv-disburse-row-error">{error}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="adv-row-actions">
                        <button
                          type="button"
                          className="adv-disburse-btn compact"
                          onClick={() => submitAssign(row)}
                          disabled={assignDisabled}
                        >
                          {busy && submittingAction === "assign"
                            ? "جاري التوجيه..."
                            : "توجيه"}
                        </button>
                        <button
                          type="button"
                          className="adv-disburse-btn compact danger"
                          onClick={() => submitCancel(row)}
                          disabled={Boolean(submittingId)}
                        >
                          {busy && submittingAction === "cancel"
                            ? "جاري الإلغاء..."
                            : "إلغاء"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="is-total-label">
                  الإجمالي ({filtered.length})
                </td>
                <td className="is-total-amount">{formatCashAmount(totalAmount)}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
